import React, { createContext, useContext, ReactNode } from 'react';
import useTranslation from '@/hooks/use-translation';
import { NavbarVariantStateEnum } from '@/constants/navbar';
import { ScreenHint } from '@/types/auth';

type NavbarContextType = {
  navState: NavbarVariantStateEnum;
  isDark: boolean;
  isAuthenticated: boolean;
  handleSignUp: (event: React.MouseEvent, screenHint?: ScreenHint) => void;
  handlePressSignOut: (event: React.MouseEvent) => void;
  t: (key: string, extraData?: {}) => string;
};

const NavbarContext = createContext<NavbarContextType | undefined>(undefined);

export const useNavbarContext = () => {
  const context = useContext(NavbarContext);
  if (context === undefined) {
    throw new Error('useNavbarContext must be used within a NavbarProvider');
  }
  return context;
};

type NavbarProviderProps = {
  children: ReactNode;
  navState: NavbarVariantStateEnum;
  isDark: boolean;
  isAuthenticated: boolean;
  handleSignUp: (event: React.MouseEvent, screenHint?: ScreenHint) => void;
  handlePressSignOut: (event: React.MouseEvent) => void;
};

export const NavbarProvider: React.FC<NavbarProviderProps> = ({
  children,
  navState = NavbarVariantStateEnum.DEFAULT,
  isDark = true,
  isAuthenticated,
  handleSignUp,
  handlePressSignOut,
}) => {
  const { t } = useTranslation('components.header.navigation-v2');

  return (
    <NavbarContext.Provider
      value={{
        navState,
        isDark,
        isAuthenticated,
        handleSignUp,
        handlePressSignOut,
        t,
      }}
    >
      {children}
    </NavbarContext.Provider>
  );
};
